import { useEffect, useState } from "react";
import { CheckCircle2, Download, Loader2, Trash2, Upload } from "lucide-react";
import { AccountLayout } from "@/components/candidate/AccountLayout";
import { apiFetch } from "@/components/candidate/shared";

const inputClass =
    "w-full border border-ink/15 bg-ivory px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-forest";

const Label = ({ children }) => (
    <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-ink/55">{children}</span>
);

const SectionLabel = ({ children }) => (
    <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-forest">
        <span className="h-px w-6 bg-champagne" aria-hidden="true" />
        {children}
    </p>
);

const SECTIONS = [
    {
        title: "Personal Information",
        fields: [
            ["full_name", "Full name", "text", true],
            ["email", "Email", "text", true],
            ["phone", "Phone / WhatsApp"],
            ["country", "Country"],
            ["city", "City"],
            ["nationality", "Nationality"],
            ["headline", "Professional headline", "text", false, "e.g. Registered Nurse — Emergency & ICU"],
        ],
    },
    {
        title: "Professional Information",
        fields: [
            ["profession", "Profession"],
            ["current_occupation", "Current occupation"],
            ["years_experience", "Years of experience", "experience"],
            ["skills", "Skills (comma separated)"],
            ["industries", "Industries (comma separated)"],
            ["preferred_countries", "Preferred countries (comma separated)"],
            ["preferred_job_types", "Preferred job types"],
            ["preferred_salary", "Preferred salary"],
        ],
    },
    {
        title: "Education",
        fields: [
            ["education_level", "Education level"],
            ["institution", "Institution"],
            ["field_of_study", "Field of study"],
            ["graduation_year", "Graduation year"],
        ],
    },
    {
        title: "Qualifications",
        fields: [
            ["certifications", "Certifications"],
            ["licences", "Professional licences"],
            ["other_qualifications", "Other qualifications"],
        ],
    },
];

const EXPERIENCE_OPTIONS = ["0–1 years", "1–3 years", "3–5 years", "5–10 years", "10+ years"];
const DOC_KINDS = [
    ["cover_letter", "Cover letter"],
    ["certificate", "Certificate"],
    ["licence", "Professional licence"],
    ["other", "Other document"],
];

export default function CandidateProfile() {
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [docKind, setDocKind] = useState("certificate");

    const load = () =>
        apiFetch("/candidate/profile")
            .then((r) => (r.ok ? r.json() : null))
            .then((p) => {
                setProfile(p);
                setForm(p || {});
            });

    useEffect(() => {
        load();
    }, []);

    const save = async () => {
        if (saving) return;
        setSaving(true);
        setSaved(false);
        await apiFetch("/candidate/profile", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(form),
        });
        setSaving(false);
        setSaved(true);
        load();
    };

    const upload = async (kind, file) => {
        if (!file || uploading) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", file);
        await apiFetch("/candidate/documents", { method: "POST", body: fd });
        setUploading(false);
        load();
    };

    const download = async (stored, original) => {
        const res = await apiFetch(`/candidate/documents/${stored}`);
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = original;
        a.click();
        URL.revokeObjectURL(url);
    };

    const remove = async (stored) => {
        await apiFetch(`/candidate/documents/${stored}`, { method: "DELETE" });
        load();
    };

    return (
        <AccountLayout title="My Profile" subtitle="Keep your professional profile current — it powers faster applications.">
            {!profile ? (
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-forest border-t-transparent" />
            ) : (
                <div className="grid gap-12 lg:grid-cols-12" data-testid="profile-page">
                    <div className="space-y-12 lg:col-span-7">
                        {SECTIONS.map((section) => (
                            <fieldset key={section.title} className="min-w-0 space-y-5">
                                <SectionLabel>{section.title}</SectionLabel>
                                <div className="grid gap-5 sm:grid-cols-2">
                                    {section.fields.map(([key, label, type, readonly, placeholder]) => (
                                        <div key={key}>
                                            <Label>{label}</Label>
                                            {type === "experience" ? (
                                                <select
                                                    data-testid={`profile-${key}`}
                                                    value={form[key] || ""}
                                                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                                                    className={`${inputClass} cursor-pointer`}
                                                >
                                                    <option value="">Select experience</option>
                                                    {EXPERIENCE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                                                </select>
                                            ) : (
                                                <input
                                                    data-testid={`profile-${key}`}
                                                    type="text"
                                                    value={form[key] || ""}
                                                    readOnly={readonly}
                                                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                                                    placeholder={placeholder || ""}
                                                    className={`${inputClass} ${readonly ? "cursor-not-allowed bg-sand text-ink/50" : ""}`}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </fieldset>
                        ))}

                        <div className="flex items-center gap-4">
                            <button
                                type="button"
                                data-testid="profile-save-btn"
                                onClick={save}
                                disabled={saving}
                                className="inline-flex items-center gap-2 bg-forest px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-ivory transition-colors hover:bg-forest-deep disabled:opacity-70"
                            >
                                {saving && <Loader2 size={14} className="animate-spin" />}
                                Save Profile
                            </button>
                            {saved && (
                                <span data-testid="profile-saved" className="inline-flex items-center gap-1.5 text-sm text-forest">
                                    <CheckCircle2 size={15} strokeWidth={1.5} />
                                    Saved
                                </span>
                            )}
                            <span className="text-xs text-ink/45">Completion: {profile.completion}%</span>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <SectionLabel>Documents</SectionLabel>
                        <div className="mt-5 space-y-6">
                            <div className="border border-ink/10 bg-sand p-6" data-testid="cv-manager">
                                <p className="text-sm font-medium text-ink">CV</p>
                                {profile.cv ? (
                                    <div className="mt-4 space-y-4">
                                        <p className="truncate text-sm text-ink/65">
                                            {profile.cv.original}
                                            <span className="block text-xs text-ink/40">
                                                Uploaded {new Date(profile.cv.uploaded_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                                            </span>
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            <button type="button" data-testid="cv-download-btn" onClick={() => download(profile.cv.stored, profile.cv.original)} className="inline-flex items-center gap-1.5 border border-forest/30 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-forest transition-colors hover:bg-forest hover:text-ivory">
                                                <Download size={13} strokeWidth={1.5} /> Download
                                            </button>
                                            <label className="inline-flex cursor-pointer items-center gap-1.5 border border-ink/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink/60 transition-colors hover:border-forest hover:text-forest">
                                                <Upload size={13} strokeWidth={1.5} /> Replace
                                                <input data-testid="cv-replace-input" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => upload("cv", e.target.files?.[0])} />
                                            </label>
                                            <button type="button" data-testid="cv-delete-btn" onClick={() => remove(profile.cv.stored)} className="inline-flex items-center gap-1.5 border border-ink/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink/45 transition-colors hover:border-ink/40">
                                                <Trash2 size={13} strokeWidth={1.5} /> Delete
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <label className="mt-4 flex cursor-pointer items-center justify-between gap-4 border border-dashed border-ink/25 bg-ivory px-4 py-4 text-sm transition-colors hover:border-forest">
                                        <span className="text-ink/45">{uploading ? "Uploading…" : "Upload your CV"}</span>
                                        <Upload size={15} strokeWidth={1.5} className="shrink-0 text-forest" />
                                        <input data-testid="cv-upload-input" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => upload("cv", e.target.files?.[0])} />
                                    </label>
                                )}
                                <p className="mt-3 text-xs text-ink/40">PDF, DOC or DOCX — up to 5 MB. Your saved CV can be reused in one click when you apply.</p>
                            </div>

                            <div className="border border-ink/10 bg-sand p-6" data-testid="docs-manager">
                                <p className="text-sm font-medium text-ink">Supporting documents</p>
                                <div className="mt-4 flex gap-2">
                                    <select data-testid="doc-kind-select" value={docKind} onChange={(e) => setDocKind(e.target.value)} className={`${inputClass} cursor-pointer`}>
                                        {DOC_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </select>
                                    <label className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 bg-forest px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-ivory transition-colors hover:bg-forest-deep">
                                        <Upload size={13} strokeWidth={1.5} /> Upload
                                        <input data-testid="doc-upload-input" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" onChange={(e) => upload(docKind, e.target.files?.[0])} />
                                    </label>
                                </div>
                                <div className="mt-4 space-y-2">
                                    {(profile.documents || []).map((doc) => (
                                        <div key={doc.stored} data-testid={`doc-row-${doc.stored}`} className="flex items-center justify-between gap-3 border border-ink/10 bg-ivory px-4 py-3">
                                            <span className="min-w-0 truncate text-sm text-ink/70">
                                                {doc.original}
                                                <span className="block text-[10px] uppercase tracking-[0.14em] text-ink/40">{doc.kind.replace("_", " ")}</span>
                                            </span>
                                            <span className="flex shrink-0 gap-1.5">
                                                <button type="button" aria-label="Download document" onClick={() => download(doc.stored, doc.original)} className="p-2 text-ink/50 transition-colors hover:text-forest">
                                                    <Download size={14} strokeWidth={1.5} />
                                                </button>
                                                <button type="button" aria-label="Delete document" data-testid={`doc-delete-${doc.stored}`} onClick={() => remove(doc.stored)} className="p-2 text-ink/40 transition-colors hover:text-ink">
                                                    <Trash2 size={14} strokeWidth={1.5} />
                                                </button>
                                            </span>
                                        </div>
                                    ))}
                                    {(profile.documents || []).length === 0 && (
                                        <p className="text-xs text-ink/45">No supporting documents yet.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AccountLayout>
    );
}
